import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { useRequirementTree } from '../../hooks/useRequirements';
import { useDebounce } from '../../hooks/useDebounce';
import type { RequirementTreeNode } from '../../services/requirementService';
import Spinner from '../ui/Spinner';

// ============================================================================
// RequirementTreeSelect — 요구사항 트리 구조 선택 컴포넌트 (CHG-029)
// ============================================================================

interface RequirementTreeSelectProps {
  projectId: string;
  value: string | null;
  onChange: (requirementId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

// --- 검색 필터: 매칭 노드 + 조상 노드 포함 ---
function filterTree(nodes: RequirementTreeNode[], query: string): RequirementTreeNode[] {
  if (!query.trim()) return nodes;
  const lowerQuery = query.toLowerCase();

  function filterNode(node: RequirementTreeNode): RequirementTreeNode | null {
    const selfMatch =
      node.requirementKey.toLowerCase().includes(lowerQuery) ||
      node.title.toLowerCase().includes(lowerQuery);

    const filteredChildren = node.children
      .map((child) => filterNode(child))
      .filter(Boolean) as RequirementTreeNode[];

    if (selfMatch || filteredChildren.length > 0) {
      return {
        ...node,
        // 자신이 매치되면 모든 자식 표시, 자식만 매치되면 매치된 자식만 표시
        children: selfMatch ? node.children : filteredChildren,
      };
    }
    return null;
  }

  return nodes.map((n) => filterNode(n)).filter(Boolean) as RequirementTreeNode[];
}

// --- 검색어 하이라이트 ---
function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, index)}
      <span className="bg-yellow-100">{text.slice(index, index + query.length)}</span>
      {text.slice(index + query.length)}
    </>
  );
}

// --- 선택된 요구사항 라벨 찾기 (재귀) ---
function findLabel(nodes: RequirementTreeNode[], id: string): string | null {
  for (const node of nodes) {
    if (node.requirementId === id) return `${node.requirementKey} ${node.title}`;
    const found = findLabel(node.children, id);
    if (found) return found;
  }
  return null;
}

export default function RequirementTreeSelect({
  projectId,
  value,
  onChange,
  placeholder = '요구사항 선택',
  disabled = false,
}: RequirementTreeSelectProps) {
  const { data: treeData, isLoading } = useRequirementTree(projectId);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 트리 로드 후 최상위 노드 기본 펼침
  useEffect(() => {
    if (treeData) {
      setExpandedNodes(new Set(treeData.map((n) => n.requirementId)));
    }
  }, [treeData]);

  // 드롭다운 열릴 때 검색창 포커스
  useEffect(() => {
    if (isOpen) {
      // 약간의 지연으로 DOM 렌더링 후 포커스
      requestAnimationFrame(() => searchInputRef.current?.focus());
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // debounce 검색
  const debouncedQuery = useDebounce(searchQuery, 200);
  const filteredTree = useMemo(
    () => filterTree(treeData ?? [], debouncedQuery),
    [treeData, debouncedQuery],
  );

  // 선택된 요구사항 라벨
  const selectedLabel = useMemo(
    () => (value && treeData ? findLabel(treeData, value) : null),
    [value, treeData],
  );

  // 펼침/접힘 토글
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  // Escape 키 닫기
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setIsOpen(false);
  };

  // --- 트리 노드 렌더링 ---
  function renderTree(nodes: RequirementTreeNode[], depth: number) {
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodes.has(node.requirementId);
      const isSelected = node.requirementId === value;
      const paddingLeft = 8 + depth * 16;

      return (
        <div key={node.requirementId}>
          <div
            className={`flex items-center gap-1 py-1.5 px-2 cursor-pointer text-sm
              ${isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
            style={{ paddingLeft }}
          >
            {/* 펼침/접힘 아이콘 */}
            {hasChildren ? (
              <button
                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(node.requirementId);
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            ) : (
              <span className="w-4 h-4 flex items-center justify-center text-gray-300 flex-shrink-0">
                ─
              </span>
            )}

            {/* 요구사항 정보 (클릭 시 선택) */}
            <div
              className="flex items-center gap-1.5 flex-1 min-w-0"
              onClick={() => {
                onChange(node.requirementId);
                setIsOpen(false);
              }}
            >
              <span className="text-sm text-gray-400 flex-shrink-0">
                <HighlightMatch text={node.requirementKey} query={debouncedQuery} />
              </span>
              <span className="truncate">
                <HighlightMatch text={node.title} query={debouncedQuery} />
              </span>
              {isSelected && <span className="text-blue-500 flex-shrink-0">✓</span>}
            </div>
          </div>

          {/* 자식 노드 (펼쳐진 경우 또는 검색 중) */}
          {hasChildren && (isExpanded || debouncedQuery.trim()) && renderTree(node.children, depth + 1)}
        </div>
      );
    });
  }

  return (
    <div ref={dropdownRef} className="relative" onKeyDown={handleKeyDown}>
      {/* 선택 영역 */}
      <div
        className={`flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm
          ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-400'}
          ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {value && !disabled && (
            <button
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* 드롭다운 */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-[320px] flex flex-col">
          {/* 검색 입력 */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 pl-8 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="요구사항 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* 트리 목록 */}
          <div className="overflow-y-auto flex-1 py-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner className="size-5" />
              </div>
            ) : filteredTree.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-6">
                {debouncedQuery.trim() ? '검색 결과가 없습니다.' : '요구사항이 없습니다.'}
              </div>
            ) : (
              renderTree(filteredTree, 0)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
